import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Star, Loader2, CheckCircle, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";

interface ReviewRequestDialogProps {
  merchantId: string;
  merchantName: string;
  totalSplits: number;
  subscriptionStartDate: string | null;
  plan: string;
}

const reviewSchema = z.object({
  rating: z.number().min(1, "Please select a rating").max(5),
  review: z.string().trim().min(20, "Review must be at least 20 characters").max(500, "Review must be less than 500 characters"),
});

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function ReviewRequestDialog({
  merchantId,
  merchantName,
  totalSplits,
  subscriptionStartDate,
  plan,
}: ReviewRequestDialogProps) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [review, setReview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    checkIfShouldShowReviewRequest();
  }, [totalSplits, subscriptionStartDate, plan]);

  const checkIfShouldShowReviewRequest = async () => {
    // Don't show if already dismissed in this session
    const dismissedKey = `review_dismissed_${merchantId}`;
    if (sessionStorage.getItem(dismissedKey)) {
      return;
    }

    // Check if already submitted a review
    const submittedKey = `review_submitted_${merchantId}`;
    if (localStorage.getItem(submittedKey)) {
      return;
    }

    // Only show for paid plans
    if (plan === "trial") {
      return;
    }

    // Check conditions: 100+ splits OR 1+ month of paid subscription
    const hasEnoughSplits = totalSplits >= 100;
    
    let hasOneMonthSubscription = false;
    if (subscriptionStartDate) {
      const startDate = new Date(subscriptionStartDate);
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      hasOneMonthSubscription = startDate <= oneMonthAgo;
    }

    if (hasEnoughSplits || hasOneMonthSubscription) {
      // Small delay to not interrupt immediately
      setTimeout(() => setOpen(true), 3000);
    }
  };

  const handleDismiss = () => {
    const dismissedKey = `review_dismissed_${merchantId}`;
    sessionStorage.setItem(dismissedKey, "true");
    setDismissed(true);
    setOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const validated = reviewSchema.parse({ rating, review });
      setSubmitting(true);

      // Send review via edge function
      const { error } = await supabase.functions.invoke("notify-merchant", {
        body: {
          type: "merchant_review",
          merchantEmail: "contact@splitkart.app",
          data: {
            merchantId,
            merchantName,
            rating: validated.rating,
            review: validated.review,
            totalSplits,
            plan,
          },
        },
      });

      if (error) throw error;

      // Mark as submitted
      localStorage.setItem(`review_submitted_${merchantId}`, "true");
      setSubmitted(true);
      toast.success("Thank you for your review!");
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        console.error("Error submitting review:", error);
        toast.error("Failed to submit review. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemindLater = () => {
    const dismissedKey = `review_dismissed_${merchantId}`;
    sessionStorage.setItem(dismissedKey, "true");
    setOpen(false);
    toast.info("We'll remind you later!");
  };

  if (dismissed) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            Share Your Experience
          </DialogTitle>
          <DialogDescription>
            You've been using Shakatah successfully! We'd love to hear your feedback.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Thank You!</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Your review helps other merchants discover Shakatah.
            </p>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </div>
        ) : (
          <div className="space-y-6 pt-4">
            {/* Rating */}
            <div className="space-y-2">
              <Label>How would you rate Shakatah?</Label>
              <div className="flex gap-2 justify-center py-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${
                        star <= (hoveredRating || rating)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-muted-foreground/30"
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-muted-foreground">
                {rating === 0 && "Click to rate"}
                {rating === 1 && "Poor"}
                {rating === 2 && "Fair"}
                {rating === 3 && "Good"}
                {rating === 4 && "Very Good"}
                {rating === 5 && "Excellent!"}
              </p>
            </div>

            {/* Review Text */}
            <div className="space-y-2">
              <Label htmlFor="review">Tell us about your experience</Label>
              <Textarea
                id="review"
                placeholder="What do you like most about Shakatah? How has it helped your business?"
                value={review}
                onChange={(e) => setReview(e.target.value)}
                rows={4}
              />
              <p className="text-xs text-muted-foreground text-right">
                {review.length}/500
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleRemindLater}
                className="flex-1"
              >
                Remind Me Later
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || rating === 0}
                className="flex-1"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Review"
                )}
              </Button>
            </div>

            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
